import type { CharacterAttributes, CoreStats } from './types/game';

/**
 * Base attack range component applied when calculating weapon-using attacks.
 * When a character has a weapon, effective range = this + weapon.combat.range.
 * Deliberately small to incentivize weapon use.
 */
export const DEFAULT_ATTACK_RANGE = 0.25 as const;

/**
 * Fallback attack range for characters with no weapons equipped at all.
 * When a character truly has no weapon, this is their only attack reach.
 * Higher than DEFAULT_ATTACK_RANGE to allow reasonable self-defense without a weapon.
 */
export const UNARMED_FALLBACK_RANGE = 1.0 as const;

/**
 * Damage midpoint for a character attacking with nothing equipped and no
 * authored `naturalBaseDamage` of its own.
 *
 * Deliberately low. It used to be 10 — above the shortsword's 7 — which meant
 * that once cadences were shortened, bare fists out-damaged an actual weapon
 * and equipping one was a downgrade. A weapon should always be an upgrade.
 */
export const DEFAULT_UNARMED_BASE_DAMAGE = 4 as const;

/** Maximum distance in tile units for a direct world-object interaction. */
export const DEFAULT_INTERACTION_RANGE = 1.25 as const;

/**
 * Radius in tile units of the safe exit zone around the map exit anchor.
 * Covers 2 tiles outward in each direction from the exit center.
 * Players inside this radius are invisible to monsters and protected from aggro.
 */
export const EXIT_ZONE_RADIUS_TILES = 2.5 as const;

/** Default server port. */
export const SERVER_PORT = 3000 as const;

/** Where the server serves Socket.IO: the party, character and crucible namespaces. */
export const SOCKET_IO_PATH = '/socket.io';

/**
 * Baseline player movement speed in tile units per second, before the
 * `coreStats.speed`-driven adjustment applied by `resolveMoveSpeed()`
 * (see `movement-speed.ts`). Shared by the crucible-host server (path/
 * direction stepping) and the client (local prediction) so both sides
 * agree on a character's effective speed.
 */
export const BASE_PLAYER_MOVE_SPEED = 3.75 as const;

/** Maximum players per crucible. */
export const MAX_PARTY_SIZE = 8 as const;
/** The fewest players a crucible's run starts with when its creator sets none. */
export const DEFAULT_MIN_PLAYERS = 1 as const;

/** Longest party chat message; the server cuts longer ones, and the chat input stops there. */
export const PARTY_CHAT_MAX_LENGTH = 400;

/** Base carried slots before account/strength/equipment bonuses are applied. */
export const BASE_CARRIED_SLOTS = 5 as const;

/** Hard upper bound for carried inventory slots, regardless of bonuses. */
export const MAX_CARRIED_SLOTS = 18 as const;

/** Base storage slots before account bonuses are applied. */
export const BASE_STORAGE_SLOTS = 20 as const;

/** Attribute point budget at character creation. */
export const CREATION_ATTRIBUTE_POINTS = 3 as const;

/** Value every attribute starts at before creation points are allocated. */
export const CREATION_ATTRIBUTE_STARTING_VALUE = 1 as const;

/**
 * A freshly created character: every attribute at
 * `CREATION_ATTRIBUTE_STARTING_VALUE` plus the `CREATION_ATTRIBUTE_POINTS`
 * spread across the physical attributes. Used as the power baseline
 * (`player-power.ts`); never a stand-in for attributes a request left out.
 * Keep its point total equal to 6 × starting value + creation points.
 */
export const FRESH_CHARACTER_ATTRIBUTES: Readonly<CharacterAttributes> = {
    vitality: 2,
    strength: 2,
    agility: 1,
    quickness: 2,
    intellect: 1,
    endurance: 1,
};

/** Minimum and maximum values for a character attribute. */
export const ATTRIBUTE_MIN = 1 as const;
export const ATTRIBUTE_MAX = 10 as const;

/** Every attribute at `ATTRIBUTE_MIN`: an allocation before any point is spent (a respec's start, an NPC's base). */
export const MINIMUM_CHARACTER_ATTRIBUTES: Readonly<CharacterAttributes> = {
    vitality: ATTRIBUTE_MIN,
    strength: ATTRIBUTE_MIN,
    agility: ATTRIBUTE_MIN,
    quickness: ATTRIBUTE_MIN,
    intellect: ATTRIBUTE_MIN,
    endurance: ATTRIBUTE_MIN,
};

/** Highest skill level. */
export const SKILL_LEVEL_MAX = 10 as const;

/** Hit chance, in percent, when attacker and defender effectiveness are even. */
export const BASE_HIT_CHANCE_PERCENT = 70;
/** Lowest hit chance, in percent, however outmatched the attacker is. */
export const MIN_HIT_CHANCE_PERCENT = 5;
/** Highest hit chance, in percent, however outmatched the defender is. */
export const MAX_HIT_CHANCE_PERCENT = 95;

/** Canonical attribute descriptions for UI and API consumers. */
export const CHARACTER_ATTRIBUTE_DESCRIPTIONS: Record<keyof CharacterAttributes, string> = {
    vitality: 'Physical resilience and recovery; improves survivability under damage and how much essence Draw restores.',
    strength: 'Raw physical force; improves melee impact and other power-based actions.',
    agility: 'Reflex control and precision; improves evasion, accuracy-adjacent outcomes, and nimble execution.',
    quickness: 'Movement and action tempo; improves how quickly a character repositions and performs actions.',
    intellect: 'Understanding of complex systems and how they connect, your own feelings included. Sets the size of your essence pool and feeds Conviction.',
    endurance: 'Endurance and resilience; improves essence pool capacity and sustainability in prolonged encounters.',
};

/** Each core stat's name as players see it. */
export const CORE_STAT_LABELS: Readonly<Record<keyof CoreStats, string>> = {
    melee: 'Melee',
    range: 'Range',
    conviction: 'Conviction',
    dodge: 'Dodge',
    speed: 'Speed',
    resistance: 'Resistance',
};

/** Canonical core stat descriptions for UI and API consumers. */
export const CORE_STAT_DESCRIPTIONS: Record<keyof CoreStats, string> = {
    melee: 'Proficiency in close-quarters weapon combat.',
    range: 'Proficiency in projectile and distance-based weapon combat.',
    conviction: 'How wholly you mean what you shape: the hit chance and damage of your Essence abilities.',
    dodge: 'Defensive reflex score for avoiding physical damage.',
    speed: 'Tempo score for action cadence, turn priority, and reposition timing.',
    resistance: 'Defensive mitigation score for reducing or avoiding non-physical (essence) damage.',
};
